import * as intrfce from "../Interfaces";

export type AddressSelection = "yourAddress" | "recommendedAddress";
export type BillingAddressValidationOutcome =
  | "valid"
  | "invalid"
  | "unavailable";

const normalize = (value?: string): string =>
  value?.trim().toUpperCase() || "";

const isUnitedStates = (country?: string): boolean =>
  ["US", "USA", "UNITED STATES"].includes(normalize(country));

const isCanada = (country?: string): boolean =>
  ["CA", "CAN", "CANADA"].includes(normalize(country));

export const isStrictBillingAddressCountry = (country?: string): boolean =>
  isUnitedStates(country) || isCanada(country);

// PREMISE_PROXIMITY is still sufficiently precise for this policy when the
// city, state/province, and postal-code components are individually confirmed.
const ALLOWED_ADDRESS_GRANULARITIES = new Set([
  "PREMISE",
  "SUB_PREMISE",
  "PREMISE_PROXIMITY",
]);
const ALLOWED_NEXT_ACTIONS = new Set([
  "ACCEPT",
  "CONFIRM",
  "CONFIRM_ADD_SUBPREMISES",
]);
const REQUIRED_STRICT_COMPONENTS = [
  "route",
  "locality",
  "administrative_area_level_1",
  "postal_code",
];

const getComponentMap = (validationResult: any): Map<string, any> =>
  new Map<string, any>(
    (validationResult?.result?.address?.addressComponents || []).map(
      (component: any) => [component.componentType, component]
    )
  );

const normalizePostalCode = (country?: string, value?: string): string => {
  if (isUnitedStates(country)) {
    return normalize(value).split("-")[0];
  }
  if (isCanada(country)) {
    return normalize(value).replace(/[\s-]/g, "");
  }
  return normalize(value);
};

const matchesValidatedStrictCombination = (
  validationResult: any,
  address: intrfce.iAddressFormData
): boolean => {
  const postalAddress = validationResult?.result?.address?.postalAddress;
  if (!postalAddress) {
    return false;
  }

  return (
    normalize(address.cityValue) === normalize(postalAddress.locality) &&
    normalize(address.stateValue) ===
      normalize(postalAddress.administrativeArea) &&
    normalizePostalCode(address.countryValue, address.pincode) ===
      normalizePostalCode(address.countryValue, postalAddress.postalCode) &&
    (!postalAddress.regionCode ||
      normalize(postalAddress.regionCode) === normalize(address.countryValue))
  );
};

const getStrictValidationOutcome = (
  validationResult: any,
  address: intrfce.iAddressFormData,
  allowCorrections: boolean
): BillingAddressValidationOutcome => {
  const result = validationResult?.result;
  const verdict = result?.verdict;
  if (
    !result ||
    !verdict ||
    !verdict.possibleNextAction ||
    !verdict.validationGranularity
  ) {
    return "unavailable";
  }

  if (verdict.possibleNextAction === "FIX") {
    return "invalid";
  }
  if (!ALLOWED_NEXT_ACTIONS.has(verdict.possibleNextAction)) {
    return "unavailable";
  }
  if (!ALLOWED_ADDRESS_GRANULARITIES.has(verdict.validationGranularity)) {
    return "invalid";
  }
  if (
    verdict.addressComplete === false &&
    verdict.possibleNextAction !== "CONFIRM_ADD_SUBPREMISES"
  ) {
    return "invalid";
  }

  const componentMap = getComponentMap(validationResult);
  for (const componentType of REQUIRED_STRICT_COMPONENTS) {
    const component = componentMap.get(componentType);
    if (!component) {
      return "unavailable";
    }
    if (component.confirmationLevel !== "CONFIRMED") {
      return "invalid";
    }
  }

  const postalAddress = result.address?.postalAddress;
  if (
    !postalAddress?.locality ||
    !postalAddress.administrativeArea ||
    !postalAddress.postalCode
  ) {
    return "unavailable";
  }
  if (!matchesValidatedStrictCombination(validationResult, address)) {
    return "invalid";
  }

  if (verdict.hasReplacedComponents && !allowCorrections) {
    const replacedComponents = (result.address?.addressComponents || [])
      .filter((component: any) => component.replaced)
      .map((component: any) => component.componentType);
    const validatedPostalCode = componentMap.get("postal_code")?.componentName
      ?.text;
    const isPostalFormattingOnly =
      replacedComponents.length === 1 &&
      replacedComponents[0] === "postal_code" &&
      normalizePostalCode(address.countryValue, address.pincode) ===
        normalizePostalCode(address.countryValue, validatedPostalCode);

    if (!isPostalFormattingOnly) {
      return "invalid";
    }
  }

  return "valid";
};

export const getBillingAddressValidationKey = (
  address: intrfce.iAddressFormData
): string =>
  JSON.stringify({
    address: address.address || "",
    line2: address.line2 || "",
    cityValue: address.cityValue || "",
    stateValue: address.stateValue || "",
    pincode: address.pincode || "",
    countryValue: address.countryValue || "",
  });

export const invalidateBillingAddressConfirmation = (
  confirmedKey: string | null,
  currentAddressKey: string
): string | null =>
  confirmedKey !== null && confirmedKey !== currentAddressKey
    ? null
    : confirmedKey;

export const createBillingAddressValidationPayload = (
  address: intrfce.iAddressFormData,
  locale: string
) => {
  const addressLines = [address.address || ""];
  if (address.line2?.trim()) {
    addressLines.push(address.line2);
  }

  return {
    addressLines,
    locality: address.cityValue || "",
    administrativeArea: address.stateValue || "",
    postalCode: address.pincode || "",
    regionCode: address.countryValue || "",
    languageCode: locale.substring(0, 2) || "en",
  };
};

export const getRecommendedBillingAddress = (
  validationResult: any,
  originalAddress: intrfce.iAddressFormData
): intrfce.iAddressFormData | null => {
  const postalAddress = validationResult?.result?.address?.postalAddress;
  if (!postalAddress) {
    return null;
  }
  const recommendedCountry =
    postalAddress.regionCode || originalAddress.countryValue;
  if (
    !postalAddress.addressLines?.[0] ||
    !postalAddress.locality ||
    (isStrictBillingAddressCountry(recommendedCountry) &&
      (!postalAddress.postalCode || !postalAddress.administrativeArea))
  ) {
    return null;
  }

  return {
    ...originalAddress,
    address: postalAddress.addressLines?.[0] || originalAddress.address || "",
    line2: postalAddress.addressLines?.[1] || "",
    cityValue: postalAddress.locality || originalAddress.cityValue || "",
    stateValue:
      postalAddress.administrativeArea || originalAddress.stateValue || "",
    pincode: postalAddress.postalCode || originalAddress.pincode || "",
    countryValue: recommendedCountry || "",
    countryName: originalAddress.countryName,
    county: postalAddress.sublocality || originalAddress.county,
  };
};

export const getBillingAddressValidationOutcome = (
  validationResult: any,
  userAddress: intrfce.iAddressFormData
): BillingAddressValidationOutcome => {
  if (!isStrictBillingAddressCountry(userAddress.countryValue)) {
    return "valid";
  }

  return getStrictValidationOutcome(validationResult, userAddress, false);
};

export const canConfirmEnteredBillingAddress = (
  outcome: BillingAddressValidationOutcome
): boolean => outcome !== "invalid";

export const isBillingAddressValidationUnavailable = (
  outcome: BillingAddressValidationOutcome
): boolean => outcome === "unavailable";

export const isBillingAddressCombinationValid = (
  validationResult: any,
  userAddress: intrfce.iAddressFormData
): boolean =>
  getBillingAddressValidationOutcome(validationResult, userAddress) ===
  "valid";

export const isRecommendedBillingAddressValid = (
  validationResult: any,
  recommendedAddress: intrfce.iAddressFormData | null
): boolean => {
  if (!recommendedAddress) {
    return false;
  }

  if (!isStrictBillingAddressCountry(recommendedAddress.countryValue)) {
    return true;
  }

  return (
    getStrictValidationOutcome(
      validationResult,
      recommendedAddress,
      true
    ) === "valid"
  );
};
